import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Divider, Table, TableHead,
  TableRow, TableCell, } from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  CATERERS_QUERY,
  MENU_ITEMS_QUERY,
  CREATE_CATERER_MUTATION,
  UPDATE_CATERER_MUTATION,
  DELETE_CATERER_MUTATION,
  ADD_MENU_ITEM_MUTATION,
  UPDATE_MENU_ITEM_MUTATION,
  DELETE_MENU_ITEM_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const CUISINES = ['south_indian','north_indian','international','chinese','continental','street_food'];
const COURSES  = ['starter','main','dessert','beverage','snack','breakfast','dinner'];
const EMPTY_CATERER = { name: '', contactPerson: '', phone: '', email: '', cuisineTypes: [], minGuests: 100, maxGuests: 2000, pricePerPersonVeg: '', pricePerPersonNonveg: '', city: '', description: '' };
const EMPTY_ITEM    = { itemName: '', course: 'main', cuisineType: 'south_indian', isVeg: true, pricePerPerson: '', sortOrder: 0 };

export default function AdminCaterersPage() {
  const [selectedId, setSelectedId]   = useState(null);
  const [catererDialog, setCatererDialog] = useState(false);
  const [editCaterer, setEditCaterer] = useState(null);
  const [catererForm, setCatererForm] = useState(EMPTY_CATERER);
  const [itemDialog, setItemDialog]   = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [itemForm, setItemForm]       = useState(EMPTY_ITEM);
  const [error, setError] = useState('');

  const { data, loading, refetch } = useQuery(CATERERS_QUERY, { fetchPolicy: 'cache-and-network' });
  const caterers  = data?.caterers || [];
  const selected  = caterers.find(c => c.id === selectedId) || null;

  const { data: menuData, refetch: refetchMenu } = useQuery(MENU_ITEMS_QUERY, {
    variables: { catererId: selectedId },
    skip: !selectedId,
    fetchPolicy: 'cache-and-network',
  });
  const menuItems = menuData?.menuItems || [];

  const [createCaterer] = useMutation(CREATE_CATERER_MUTATION, {
    onCompleted: (d) => { setCatererDialog(false); setCatererForm(EMPTY_CATERER); refetch(); setSelectedId(d.createCaterer.id); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateCaterer] = useMutation(UPDATE_CATERER_MUTATION, {
    onCompleted: () => { setCatererDialog(false); setEditCaterer(null); setCatererForm(EMPTY_CATERER); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteCaterer] = useMutation(DELETE_CATERER_MUTATION, { onCompleted: () => { setSelectedId(null); refetch(); } });

  const [addItem]    = useMutation(ADD_MENU_ITEM_MUTATION,    { onCompleted: () => { setItemDialog(false); setItemForm(EMPTY_ITEM); refetchMenu(); setError(''); }, onError: e => setError(e.message) });
  const [updateItem] = useMutation(UPDATE_MENU_ITEM_MUTATION, { onCompleted: () => { setItemDialog(false); setEditItem(null); setItemForm(EMPTY_ITEM); refetchMenu(); setError(''); }, onError: e => setError(e.message) });
  const [deleteItem] = useMutation(DELETE_MENU_ITEM_MUTATION, { onCompleted: () => refetchMenu() });

  const openCreateCaterer = () => { setEditCaterer(null); setCatererForm(EMPTY_CATERER); setCatererDialog(true); };
  const openEditCaterer   = (c) => { setEditCaterer(c); setCatererForm({ name: c.name, contactPerson: c.contactPerson || '', phone: c.phone || '', email: c.email || '', cuisineTypes: c.cuisineTypes || [], minGuests: c.minGuests, maxGuests: c.maxGuests, pricePerPersonVeg: c.pricePerPersonVeg || '', pricePerPersonNonveg: c.pricePerPersonNonveg || '', city: c.city || '', description: c.description || '' }); setCatererDialog(true); };
  const saveCaterer = () => {
    const vars = { ...catererForm, minGuests: Number(catererForm.minGuests), maxGuests: Number(catererForm.maxGuests), pricePerPersonVeg: catererForm.pricePerPersonVeg ? Number(catererForm.pricePerPersonVeg) : null, pricePerPersonNonveg: null }; // vegetarian only
    if (editCaterer) updateCaterer({ variables: { id: editCaterer.id, ...vars } });
    else createCaterer({ variables: vars });
  };

  const openCreateItem = () => { setEditItem(null); setItemForm(EMPTY_ITEM); setItemDialog(true); };
  const openEditItem   = (item) => { setEditItem(item); setItemForm({ itemName: item.itemName, course: item.course, cuisineType: item.cuisineType, isVeg: true, pricePerPerson: item.pricePerPerson || '', sortOrder: item.sortOrder }); setItemDialog(true); };
  const saveItem = () => {
    const vars = { ...itemForm, pricePerPerson: itemForm.pricePerPerson ? Number(itemForm.pricePerPerson) : null, sortOrder: Number(itemForm.sortOrder) || 0 };
    if (editItem) updateItem({ variables: { id: editItem.id, ...vars } });
    else addItem({ variables: { catererId: selectedId, ...vars } });
  };

  const toggleCuisine = (c) => setCatererForm(f => ({ ...f, cuisineTypes: f.cuisineTypes.includes(c) ? f.cuisineTypes.filter(x => x !== c) : [...f.cuisineTypes, c] }));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Caterers Admin</Typography>
          <Typography variant="body2" color="text.secondary">Manage caterer profiles and menu item catalogues.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateCaterer}>New Caterer</Button>
      </Stack>

      <ContractedVendorsSection category="catering" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Caterer list */}
        <Grid item xs={12} md={4}>
          <Stack spacing={1.5}>
            {caterers.length === 0 && !loading && <Alert severity="info">No caterers yet.</Alert>}
            <Paged items={caterers}>{rows => rows.map(c => (
              <Card key={c.id} sx={{ cursor: 'pointer', border: '2px solid', borderColor: c.id === selectedId ? 'primary.main' : 'divider' }} onClick={() => setSelectedId(c.id === selectedId ? null : c.id)}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.city} • {(c.cuisineTypes || []).join(', ')}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip label={`${(c.menuItems || []).length} items`} size="small" icon={<MenuBookIcon sx={{ fontSize: '12px !important' }} />} />
                        {c.rating && <Chip label={`Rating ${c.rating}`} size="small" color="warning" />}
                      </Stack>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); openEditCaterer(c); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={e => { e.stopPropagation(); deleteCaterer({ variables: { id: c.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}</Paged>
          </Stack>
        </Grid>

        {/* Menu items panel */}
        <Grid item xs={12} md={8}>
          {!selected && <Alert severity="info">Select a caterer to manage its menu items.</Alert>}
          {selected && (
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}><RestaurantIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{selected.name} — Menu</Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreateItem}>Add Item</Button>
                </Stack>
                {menuItems.length === 0 && <Alert severity="info">No menu items yet. Click Add Item.</Alert>}
                {menuItems.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Course</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>₹/pp</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {menuItems.map(item => (
                        <TableRow key={item.id} hover>
                          <TableCell><Typography variant="body2">{item.itemName}</Typography></TableCell>
                          <TableCell><Chip label={item.course} size="small" variant="outlined" /></TableCell>
                          <TableCell><Chip label="Veg" size="small" color="success" variant="outlined" /></TableCell>
                          <TableCell><Typography variant="body2">{item.pricePerPerson ? `₹${item.pricePerPerson}` : '—'}</Typography></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.25}>
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditItem(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteItem({ variables: { id: item.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Caterer dialog */}
      <Dialog open={catererDialog} onClose={() => setCatererDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCaterer ? 'Edit Caterer' : 'New Caterer'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Caterer Name" value={catererForm.name} onChange={e => setCatererForm(f => ({ ...f, name: e.target.value }))} fullWidth required autoFocus />
            <Stack direction="row" spacing={2}>
              <TextField label="Contact Person" value={catererForm.contactPerson} onChange={e => setCatererForm(f => ({ ...f, contactPerson: e.target.value }))} fullWidth />
              <TextField label="Phone" value={catererForm.phone} onChange={e => setCatererForm(f => ({ ...f, phone: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Email" value={catererForm.email} onChange={e => setCatererForm(f => ({ ...f, email: e.target.value }))} fullWidth />
              <TextField label="City" value={catererForm.city} onChange={e => setCatererForm(f => ({ ...f, city: e.target.value }))} fullWidth />
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">Cuisine Types</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                {CUISINES.map(c => (
                  <Chip key={c} label={c.replace(/_/g,' ')} onClick={() => toggleCuisine(c)} color={catererForm.cuisineTypes.includes(c) ? 'primary' : 'default'} variant={catererForm.cuisineTypes.includes(c) ? 'filled' : 'outlined'} />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={2}>
              <TextField label="Min Guests" type="number" value={catererForm.minGuests} onChange={e => setCatererForm(f => ({ ...f, minGuests: e.target.value }))} fullWidth />
              <TextField label="Max Guests" type="number" value={catererForm.maxGuests} onChange={e => setCatererForm(f => ({ ...f, maxGuests: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="₹/person (Veg)" type="number" value={catererForm.pricePerPersonVeg} onChange={e => setCatererForm(f => ({ ...f, pricePerPersonVeg: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Description" value={catererForm.description} onChange={e => setCatererForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCatererDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={saveCaterer} disabled={!catererForm.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Menu item dialog */}
      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Item Name" value={itemForm.itemName} onChange={e => setItemForm(f => ({ ...f, itemName: e.target.value }))} fullWidth required autoFocus />
            <Stack direction="row" spacing={2}>
              <TextField select label="Course" value={itemForm.course} onChange={e => setItemForm(f => ({ ...f, course: e.target.value }))} fullWidth>
                {COURSES.map(c => <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>)}
              </TextField>
              <TextField select label="Cuisine" value={itemForm.cuisineType} onChange={e => setItemForm(f => ({ ...f, cuisineType: e.target.value }))} fullWidth>
                {CUISINES.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g,' ')}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="₹/person" type="number" value={itemForm.pricePerPerson} onChange={e => setItemForm(f => ({ ...f, pricePerPerson: e.target.value }))} fullWidth />
              <TextField label="Sort Order" type="number" value={itemForm.sortOrder} onChange={e => setItemForm(f => ({ ...f, sortOrder: e.target.value }))} fullWidth />
            </Stack>
            {/* Menu items are always vegetarian (the platform offers no non-veg) */}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setItemDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={saveItem} disabled={!itemForm.itemName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
